/**
 * GLB document builder — client-side STL/OBJ → GLB with Draco compression.
 *
 * Matches backend stlToGlb.js exactly:
 *   - POSITION (VEC3 float32), NORMAL (VEC3 float32), COLOR_0 (VEC4 float32 AO)
 *   - PBR material: bone [0.72, 0.65, 0.55], metallic 0, roughness 0.95
 *   - Draco compression via KHR_draco_mesh_compression (quantizePosition: 14)
 *
 * Draco encoder/decoder are loaded lazily from /public/draco/ (vendored from
 * the draco3dgltf npm package). Browser script-tag load, not bundled — avoids
 * Vite's static-analysis issue with the Emscripten Node branch's `require('fs')`.
 */

import { Document, WebIO } from '@gltf-transform/core'
import { KHRDracoMeshCompression } from '@gltf-transform/extensions'
import { draco } from '@gltf-transform/functions'

let _dracoPromise = null

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-draco-src="${src}"]`)
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve()
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', reject)
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.dataset.dracoSrc = src
    s.onload = () => { s.dataset.loaded = '1'; resolve() }
    s.onerror = reject
    document.head.appendChild(s)
  })
}

async function getDraco() {
  if (_dracoPromise) return _dracoPromise
  _dracoPromise = (async () => {
    await Promise.all([
      loadScript('/draco/draco_encoder_gltf.js'),
      loadScript('/draco/draco_decoder_gltf.js'),
    ])
    // The Emscripten modules expose global factories when loaded via <script>.
    // They return Promise-like objects that resolve to the module instance.
    // locateFile tells Emscripten where to fetch the .wasm binary.
    const locateFile = (path) => `/draco/${path}`
    const [encoder, decoder] = await Promise.all([
      window.DracoEncoderModule({ locateFile }),
      window.DracoDecoderModule({ locateFile }),
    ])
    return { encoder, decoder }
  })()
  return _dracoPromise
}

// Canonical orientation — bakes the Z-up → Y-up rotation (matches
// backend/src/services/stlToGlb.js applyCanonicalOrientation exactly) into
// vertex positions/normals so viewers never need an orientation attribute.
// Euler XYZ intrinsic, rx=80°, ry=-180°, rz=0°. Callers must run this BEFORE
// extractMetadata so UI-displayed dimensions align with the final rendered axes.
const CANON_COS80 = 0.17365
const CANON_SIN80 = 0.98481
export function applyCanonicalOrientation(positions, normals) {
  for (let i = 0; i < positions.length; i += 3) {
    const vy = positions[i + 1]
    const vz = positions[i + 2]
    positions[i]     = -positions[i]
    positions[i + 1] = CANON_COS80 * vy + CANON_SIN80 * vz
    positions[i + 2] = CANON_SIN80 * vy - CANON_COS80 * vz
  }
  for (let i = 0; i < normals.length; i += 3) {
    const vy = normals[i + 1]
    const vz = normals[i + 2]
    normals[i]     = -normals[i]
    normals[i + 1] = CANON_COS80 * vy + CANON_SIN80 * vz
    normals[i + 2] = CANON_SIN80 * vy - CANON_COS80 * vz
  }
}

export async function buildGLB(positions, normals, vertexColors) {
  const doc = new Document()
  doc.createExtension(KHRDracoMeshCompression)
  const buffer = doc.createBuffer()

  const posAccessor = doc.createAccessor()
    .setType('VEC3').setArray(positions).setBuffer(buffer)
  const normAccessor = doc.createAccessor()
    .setType('VEC3').setArray(normals).setBuffer(buffer)
  const colorAccessor = doc.createAccessor()
    .setType('VEC4').setArray(vertexColors).setBuffer(buffer)

  const material = doc.createMaterial()
    .setBaseColorFactor([0.72, 0.65, 0.55, 1.0])
    .setMetallicFactor(0.0)
    .setRoughnessFactor(0.95)

  const prim = doc.createPrimitive()
    .setAttribute('POSITION', posAccessor)
    .setAttribute('NORMAL', normAccessor)
    .setAttribute('COLOR_0', colorAccessor)
    .setMaterial(material)

  doc.createScene().addChild(doc.createNode().setMesh(doc.createMesh().addPrimitive(prim)))

  const { encoder, decoder } = await getDraco()
  const io = new WebIO()
    .registerExtensions([KHRDracoMeshCompression])
    .registerDependencies({
      'draco3d.encoder': encoder,
      'draco3d.decoder': decoder,
    })

  await doc.transform(draco({ quantizePosition: 14 }))
  return await io.writeBinary(doc)
}

/**
 * Extract metadata from positions.
 */
export function extractMetadata(positions, faceCount) {
  const vertexCount = positions.length / 3
  let minX = Infinity, minY = Infinity, minZ = Infinity
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity

  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1], z = positions[i + 2]
    if (x < minX) minX = x; if (x > maxX) maxX = x
    if (y < minY) minY = y; if (y > maxY) maxY = y
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z
  }

  return {
    vertexCount,
    faceCount,
    dimensions: {
      x: Math.round((maxX - minX) * 100) / 100,
      y: Math.round((maxY - minY) * 100) / 100,
      z: Math.round((maxZ - minZ) * 100) / 100,
    },
  }
}
